import { FC } from 'react';
import { NumericFormat } from 'react-number-format';
import { TextField, TextFieldProps } from '@mui/material';

type MuiTextFieldForNumericFormat = Omit<TextFieldProps, 'defaultValue'>;

export interface IntegerInputProps extends Omit<
  TextFieldProps,
  'type' | 'onChange' | 'defaultValue'
> {
  /**
   * Called with the coerced value after each accepted keystroke:
   *   - `number`  for any entered integer
   *   - `null`    when the field is empty
   */
  onChange?: (value: number | null) => void;
  value: number | null;
  min?: number;
  max?: number;
}

/**
 * A TextField component for integer input backed by react-number-format.
 * Rejects decimal points and non-numeric characters automatically.
 */
export const IntegerInput: FC<IntegerInputProps> = ({
  onChange,
  value,
  min,
  max,
  ...textFieldProps
}) => {
  const { slotProps: existingSlotProps, ...restProps } = textFieldProps;

  return (
    <NumericFormat
      {...restProps}
      customInput={
        TextField as React.ComponentType<MuiTextFieldForNumericFormat>
      }
      value={value}
      decimalScale={0}
      allowNegative={false}
      inputMode='numeric'
      onValueChange={({ floatValue }) => {
        onChange?.(floatValue ?? null);
      }}
      slotProps={{
        ...existingSlotProps,
        htmlInput: {
          min,
          max,
          ...existingSlotProps?.htmlInput,
        },
      }}
    />
  );
};
